import React, { useState } from 'react';
import { LogOutIcon, UserIcon, MenuIcon, XIcon, SendIcon, CalendarDaysIcon } from 'lucide-react';
import { User, AppView, Store, Brand, BrandRequest, ActivityLogEntry } from '../../data/types';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { SearchBar } from './SearchBar';
import { NotificationBell } from './NotificationBell';
interface HeaderProps {
  user: User | null;
  onLogout: () => void;
  currentView?: AppView;
  onNavigate?: (view: AppView) => void;
  draftCount?: number;
  stores?: Store[];
  brands?: Brand[];
  brandRequests?: BrandRequest[];
  activityEntries?: ActivityLogEntry[];
  unreadCount?: number;
  onMarkAllRead?: () => void;
  onMarkRead?: (id: string) => void;
  onSelectStore?: (store: Store) => void;
  onSelectBrand?: (brand: Brand) => void;
  onRequestMediaSpace?: () => void;
}
export function Header({
  user,
  onLogout,
  currentView,
  onNavigate,
  draftCount = 0,
  stores = [],
  brands = [],
  brandRequests = [],
  activityEntries = [],
  unreadCount = 0,
  onMarkAllRead = () => {},
  onMarkRead = () => {},
  onSelectStore,
  onSelectBrand,
  onRequestMediaSpace
}: HeaderProps) {
  const isAdmin = user?.role === 'admin';
  const isGrapevine = user?.role === 'grapevine';
  const isInternal = isAdmin || isGrapevine;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const handleNavClick = (view: AppView) => {
    if (onNavigate) {
      onNavigate(view);
      setMobileMenuOpen(false);
    }
  };
  return <header className="h-16 bg-white border-b border-border px-4 md:px-6 flex items-center justify-between sticky top-0 z-30" data-id="element-870">
      <div className="flex items-center gap-6 flex-1" data-id="element-871">
        <div className="flex items-center gap-2" data-id="element-872">
          {isInternal && <button className="md:hidden p-1 mr-2 text-text-secondary hover:text-primary rounded-md" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} data-id="element-873">
              {mobileMenuOpen ? <XIcon className="w-5 h-5" data-id="element-874" /> : <MenuIcon className="w-5 h-5" data-id="element-875" />}
            </button>}
          {isGrapevine ? <img src="https://cdn.magicpatterns.com/uploads/p8LJ2RFGdVthnquHL1oesF/GV_Icon.png" alt="Grapevine Media" className="h-8 md:h-9 w-auto" data-id="element-876" /> : <img src="https://cdn.magicpatterns.com/uploads/4YqtTeUHiVXRLELZ1R2DtX/4dngqz0syxbaj28.png" alt="Woolworths" className="h-8 md:h-9 w-auto" data-id="element-877" />}
          <span className="hidden sm:inline-block text-[10px] font-semibold uppercase tracking-widest text-text-tertiary bg-surface-warm px-2 py-0.5 rounded-md border border-border" data-id="element-878">
            {isGrapevine ? 'Platform Admin' : 'Media Planner'}
          </span>
        </div>

        {isInternal && onNavigate && <nav className="hidden md:flex items-center gap-1 ml-4" data-id="element-879">
            {isAdmin && <>
                <button onClick={() => handleNavClick('admin-dashboard')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${currentView === 'admin-dashboard' || currentView === 'store-timeline' ? 'bg-surface-warm text-primary' : 'text-text-secondary hover:text-primary hover:bg-surface'}`} data-id="element-880">
                  Dashboard
                </button>
                <button onClick={() => handleNavClick('booking-calendar')} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${currentView === 'booking-calendar' ? 'bg-primary text-white' : 'bg-primary/10 text-primary hover:bg-primary/20'}`} data-id="element-881">
                  <CalendarDaysIcon className="w-4 h-4" data-id="element-882" />
                  Booking Calendar
                </button>
                <button onClick={() => handleNavClick('admin-requests')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${currentView === 'admin-requests' ? 'bg-surface-warm text-primary' : 'text-text-secondary hover:text-primary hover:bg-surface'}`} data-id="element-883">
                  Brand Requests
                  {draftCount > 0 && <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold bg-requested text-white rounded-full" data-id="element-884">
                      {draftCount}
                    </span>}
                </button>
                <button onClick={() => handleNavClick('campaign-calendar')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${currentView === 'campaign-calendar' ? 'bg-surface-warm text-primary' : 'text-text-secondary hover:text-primary hover:bg-surface'}`} data-id="element-885">
                  Campaigns
                </button>
              </>}
            {isGrapevine && <>
                <button onClick={() => handleNavClick('grapevine-dashboard')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${currentView === 'grapevine-dashboard' || currentView === 'store-timeline' ? 'bg-gray-100 text-gray-900' : 'text-text-secondary hover:text-gray-900 hover:bg-surface'}`} data-id="element-886">
                  Dashboard
                </button>
                <button onClick={() => handleNavClick('booking-calendar')} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${currentView === 'booking-calendar' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`} data-id="element-887">
                  <CalendarDaysIcon className="w-4 h-4" data-id="element-888" />
                  Booking Calendar
                </button>
                <button onClick={() => handleNavClick('admin-requests')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${currentView === 'admin-requests' ? 'bg-gray-100 text-gray-900' : 'text-text-secondary hover:text-gray-900 hover:bg-surface'}`} data-id="element-889">
                  Brand Requests
                </button>
                <button onClick={() => handleNavClick('campaign-calendar')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${currentView === 'campaign-calendar' ? 'bg-gray-100 text-gray-900' : 'text-text-secondary hover:text-gray-900 hover:bg-surface'}`} data-id="element-890">
                  Campaigns
                </button>
                <button onClick={() => handleNavClick('weekly-allocation')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${currentView === 'weekly-allocation' ? 'bg-gray-100 text-gray-900' : 'text-text-secondary hover:text-gray-900 hover:bg-surface'}`} data-id="element-891">
                  Allocation Report
                </button>
              </>}
            <button onClick={() => handleNavClick('rate-cards')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${currentView === 'rate-cards' ? isGrapevine ? 'bg-gray-100 text-gray-900' : 'bg-surface-warm text-primary' : `text-text-secondary hover:${isGrapevine ? 'text-gray-900' : 'text-primary'} hover:bg-surface`}`} data-id="element-892">
              Rate Cards
            </button>
          </nav>}

        {isInternal && <div className="hidden lg:block ml-auto max-w-md w-full px-4" data-id="element-893">
            <SearchBar stores={stores} brands={brands} brandRequests={brandRequests} onSelectStore={onSelectStore} onSelectBrand={onSelectBrand} data-id="element-894" />
          </div>}

        {/* Brand: nav + search + CTA */}
        {!isInternal && <div className="ml-auto flex items-center gap-3" data-id="element-895">
            {onNavigate && <nav className="hidden md:flex items-center gap-1 mr-2" data-id="element-896">
                <button onClick={() => onNavigate('brand-dashboard')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${currentView === 'brand-dashboard' ? 'bg-surface-warm text-primary' : 'text-text-secondary hover:text-primary hover:bg-surface'}`} data-id="element-897">
                  Dashboard
                </button>
                <button onClick={() => onNavigate('campaign-calendar')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${currentView === 'campaign-calendar' ? 'bg-surface-warm text-primary' : 'text-text-secondary hover:text-primary hover:bg-surface'}`} data-id="element-898">
                  Campaigns
                </button>
              </nav>}
            {stores.length > 0 && <div className="hidden md:block w-64" data-id="element-899">
                <SearchBar stores={stores} brands={[]} brandRequests={[]} onSelectStore={onSelectStore} data-id="element-900" />
              </div>}
          </div>}
      </div>

      {user && <div className="flex items-center gap-2 md:gap-4 ml-4" data-id="element-901">
          {(isInternal || activityEntries.length > 0) && <NotificationBell entries={activityEntries} unreadCount={unreadCount} onMarkAllRead={onMarkAllRead} onMarkRead={onMarkRead} data-id="element-902" />}
          <div className="hidden sm:flex items-center gap-3" data-id="element-903">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isGrapevine ? 'bg-gray-200' : 'bg-surface-warm'}`} data-id="element-904">
              <UserIcon className={`w-4 h-4 ${isGrapevine ? 'text-gray-900' : 'text-text-secondary'}`} data-id="element-905" />
            </div>
            <div className="flex flex-col" data-id="element-906">
              <span className="text-sm font-medium text-text-primary truncate max-w-[120px]" data-id="element-907">
                {user.name}
              </span>
              {isGrapevine ? <Badge variant="standard" className="w-fit bg-gray-200 text-gray-900 border-gray-300" data-id="element-908">
                  Grapevine
                </Badge> : <Badge variant={isAdmin ? 'flagship' : 'standard'} className="w-fit" data-id="element-909">
                  {isAdmin ? 'Internal' : 'Brand'}
                </Badge>}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout} className="flex items-center gap-1.5 px-2 md:px-3" aria-label="Sign out" data-id="element-910">
            <LogOutIcon className="w-4 h-4" data-id="element-911" />
            <span className="hidden md:inline" data-id="element-912">Sign Out</span>
          </Button>
        </div>}

      {/* Mobile Navigation Menu */}
      {isInternal && mobileMenuOpen && <div className="absolute top-16 left-0 w-full bg-white border-b border-border shadow-lg md:hidden flex flex-col p-4 gap-2 z-40" data-id="element-913">
          {isAdmin && <>
              <button onClick={() => handleNavClick('admin-dashboard')} className={`px-4 py-3 text-left text-sm font-medium rounded-md ${currentView === 'admin-dashboard' || currentView === 'store-timeline' ? 'bg-surface-warm text-primary' : 'text-text-secondary'}`} data-id="element-914">
                Dashboard
              </button>
              <button onClick={() => handleNavClick('booking-calendar')} className={`px-4 py-3 text-left text-sm font-semibold rounded-md flex items-center gap-2 ${currentView === 'booking-calendar' ? 'bg-surface-warm text-primary' : 'text-primary bg-primary/5'}`} data-id="element-915">
                <CalendarDaysIcon className="w-4 h-4" data-id="element-916" />
                Booking Calendar
              </button>
              <button onClick={() => handleNavClick('admin-requests')} className={`px-4 py-3 text-left text-sm font-medium rounded-md flex items-center justify-between ${currentView === 'admin-requests' ? 'bg-surface-warm text-primary' : 'text-text-secondary'}`} data-id="element-917">
                Brand Requests
                {draftCount > 0 && <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold bg-requested text-white rounded-full" data-id="element-918">
                    {draftCount}
                  </span>}
              </button>
              <button onClick={() => handleNavClick('campaign-calendar')} className={`px-4 py-3 text-left text-sm font-medium rounded-md ${currentView === 'campaign-calendar' ? 'bg-surface-warm text-primary' : 'text-text-secondary'}`} data-id="element-919">
                Campaigns
              </button>
            </>}
          {isGrapevine && <>
              <button onClick={() => handleNavClick('grapevine-dashboard')} className={`px-4 py-3 text-left text-sm font-medium rounded-md ${currentView === 'grapevine-dashboard' || currentView === 'store-timeline' ? 'bg-gray-100 text-gray-900' : 'text-text-secondary'}`} data-id="element-920">
                Dashboard
              </button>
              <button onClick={() => handleNavClick('booking-calendar')} className={`px-4 py-3 text-left text-sm font-semibold rounded-md flex items-center gap-2 ${currentView === 'booking-calendar' ? 'bg-gray-100 text-gray-900' : 'text-gray-900 bg-gray-50'}`} data-id="element-921">
                <CalendarDaysIcon className="w-4 h-4" data-id="element-922" />
                Booking Calendar
              </button>
              <button onClick={() => handleNavClick('admin-requests')} className={`px-4 py-3 text-left text-sm font-medium rounded-md ${currentView === 'admin-requests' ? 'bg-gray-100 text-gray-900' : 'text-text-secondary'}`} data-id="element-923">
                Brand Requests
              </button>
              <button onClick={() => handleNavClick('campaign-calendar')} className={`px-4 py-3 text-left text-sm font-medium rounded-md ${currentView === 'campaign-calendar' ? 'bg-gray-100 text-gray-900' : 'text-text-secondary'}`} data-id="element-924">
                Campaigns
              </button>
              <button onClick={() => handleNavClick('weekly-allocation')} className={`px-4 py-3 text-left text-sm font-medium rounded-md ${currentView === 'weekly-allocation' ? 'bg-gray-100 text-gray-900' : 'text-text-secondary'}`} data-id="element-925">
                Allocation Report
              </button>
            </>}
          <button onClick={() => handleNavClick('rate-cards')} className={`px-4 py-3 text-left text-sm font-medium rounded-md ${currentView === 'rate-cards' ? isGrapevine ? 'bg-gray-100 text-gray-900' : 'bg-surface-warm text-primary' : 'text-text-secondary'}`} data-id="element-926">
            Rate Cards
          </button>
        </div>}
    </header>;
}